<script lang="ts" setup>
import type { TokenPrices } from '@forgd/contract/types'
import { formatValue } from '@forgd/formatter'
import { KpiStat } from '@forgd/ui-kit/blocks/kpi-stat'
import { StatValue } from '@forgd/ui-kit/stat'
import { Trend } from '@forgd/ui-kit/trend'
import { ChevronRightIcon, PencilIcon } from '@heroicons/vue/24/outline'
import { escape } from 'lodash-es'

const props = defineProps<{
  loading?: boolean
  projectId: string
  projectTicker?: string | null
  tokenPrices?: TokenPrices
  activityPath: string
}>()

const tickerLabel = computed(() => props.projectTicker || 'Token')

function formatPrice(value: number) {
  return escape(formatValue(value, 'currency'))
    .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, c => `<sub>${'₀₁₂₃₄₅₆₇₈₉'.indexOf(c)}</sub>`)
}

function goToTradeActivity() {
  navigateTo(`/project/${props.projectId}${props.activityPath}`)
}

function goToLinkCoinGecko() {
  navigateTo(`/project/${props.projectId}/token-settings#coingecko`)
}
</script>

<template>
  <KpiStat
    v-if="tokenPrices"
    :loading="loading"
    :title="`${tickerLabel} Price (USD)`"
    description="24h Change"
    :action="{ icon: ChevronRightIcon, label: 'See trade activity' }"
    @action="goToTradeActivity"
  >
    <template #value>
      <StatValue size="sm">
        <!-- eslint-disable-next-line vue/no-v-html -- value is an escaped currency string; only <sub> tags are injected -->
        <span v-html="formatPrice(tokenPrices.price ?? 0)" />
      </StatValue>
      <ChartSparkline
        v-if="tokenPrices.marketChartPrice24h"
        class="ml-1"
        :data="tokenPrices.marketChartPrice24h"
        :height="20"
        :width="40"
      />
    </template>
    <template #description>
      <span>24h Change<template v-if="tokenPrices.priceChangePercentage24h != null">:</template></span>
      <Trend
        v-if="tokenPrices.priceChangePercentage24h != null"
        :value="tokenPrices.priceChangePercentage24h"
        class="font-semibold"
      >
        {{ formatValue(tokenPrices.priceChangePercentage24h, 'percent:3') }}
      </Trend>
    </template>
  </KpiStat>

  <KpiStat
    v-else
    size="sm"
    :loading="loading"
    :title="`${tickerLabel} Price (USD)`"
    value="Not Available"
    description="Link your CoinGecko ID"
    :action="{ icon: PencilIcon, label: 'Link CoinGecko ID' }"
    @action="goToLinkCoinGecko"
  />
</template>
